import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Save, RotateCcw, Trophy, Users, Percent } from "lucide-react";
import { toast } from "sonner";

import { confirmar } from "@/components/ConfirmDialog";
const errMsg = (e) => formatApiError(e.response?.data?.detail);
const SCORES = [50, 60, 70, 75, 80, 90, 100];

const Row = ({ label, hint, children }) => (
  <div className="grid md:grid-cols-[260px_1fr] gap-2 md:gap-6 py-3 border-b border-slate-100 last:border-0">
    <div><Label className="text-slate-800">{label}</Label>{hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}</div>
    <div>{children}</div>
  </div>
);
const Sel = ({ value, onChange, children, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm min-w-[220px]">{children}</select>
);

/** Prova final do curso: configuração + resultados. */
export function ExamSettings({ course, onGoBank }) {
  const [f, setF] = useState(null);
  const [bank, setBank] = useState([]);
  const [results, setResults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [customScore, setCustomScore] = useState(false);

  const load = useCallback(() => {
    api.get(`/courses/${course.id}/exam`).then((r) => { setF(r.data); setCustomScore(!SCORES.includes(r.data.pass_score)); }).catch(() => {});
    api.get(`/courses/${course.id}/questions`).then((r) => setBank(r.data)).catch(() => {});
    api.get(`/courses/${course.id}/exam/results`).then((r) => setResults(r.data)).catch(() => setResults(null));
  }, [course.id]);
  useEffect(() => { load(); }, [load]);

  if (!f) return <div className="py-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const size = f.selection === "manual" ? f.question_ids.length : f.selection === "random" ? Math.min(f.random_count, bank.length) : bank.length;

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...f, pass_score: parseInt(f.pass_score, 10) || 70, random_count: parseInt(f.random_count, 10) || 1,
        time_limit: parseInt(f.time_limit, 10) || 0, max_attempts: parseInt(f.max_attempts, 10) || 0, wait_hours: parseInt(f.wait_hours, 10) || 0 };
      delete body.bank_size;
      setF({ ...(await api.put(`/courses/${course.id}/exam`, body)).data });
      toast.success("Prova final salva.");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const reset = async (r) => {
    if (!(await confirmar(`Liberar novas tentativas para ${r.name}? As tentativas dele(a) nesta prova serão apagadas.`))) return;
    try { await api.post(`/courses/${course.id}/exam/results/${r.user_id}/reset`); toast.success("Tentativas liberadas."); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="mt-2 space-y-6" data-testid="exam-settings">
      <p className="text-sm text-slate-500">Defina a prova final do curso. O banco tem <strong>{bank.length}</strong> questão(ões) — <button onClick={onGoBank} className="text-[#1E9E2A] font-semibold hover:underline">gerenciar banco</button>.</p>

      <div className="rounded-xl border border-slate-200 px-4">
        <Row label="Prova final ativa" hint="Quando ativa, aparece para o aluno na sala de aula.">
          <div className="flex items-center gap-2 h-10"><Switch checked={f.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="exam-enabled" /><span className="text-sm text-slate-600">{f.enabled ? "Sim" : "Não"}</span></div>
        </Row>
        <Row label="Título"><Input value={f.title} onChange={(e) => set("title", e.target.value)} className="max-w-md" /></Row>
        <Row label="Instruções" hint="Aparecem antes do aluno iniciar."><Textarea rows={3} value={f.instructions} onChange={(e) => set("instructions", e.target.value)} placeholder="Ex.: Leia com atenção. Você tem 2 tentativas." /></Row>
        <Row label="Nota mínima para aprovação" hint="Porcentagem de acerto necessária.">
          <div className="flex items-center gap-2 flex-wrap">
            <Sel value={customScore ? "outra" : String(f.pass_score)} onChange={(v) => { if (v === "outra") { setCustomScore(true); } else { setCustomScore(false); set("pass_score", parseInt(v, 10)); } }} testid="exam-pass-score">
              {SCORES.map((n) => <option key={n} value={String(n)}>{n}% de acerto</option>)}
              <option value="outra">Outra nota…</option>
            </Sel>
            {customScore && <div className="flex items-center gap-1"><Input type="number" min="1" max="100" value={f.pass_score} onChange={(e) => set("pass_score", e.target.value)} className="w-24" data-testid="exam-pass-custom" /><Percent className="h-4 w-4 text-slate-400" /></div>}
          </div>
        </Row>
        <Row label="Questões da prova" hint={`A prova terá ${size} questão(ões).`}>
          <div className="space-y-3">
            <Sel value={f.selection} onChange={(v) => set("selection", v)} testid="exam-selection">
              <option value="all">Todas as questões do banco</option>
              <option value="random">Sortear algumas do banco (cada aluno recebe uma prova diferente)</option>
              <option value="manual">Escolher as questões</option>
            </Sel>
            {f.selection === "random" && <div className="flex items-center gap-2 text-sm text-slate-600">Sortear <Input type="number" min="1" max={bank.length || 1} value={f.random_count} onChange={(e) => set("random_count", e.target.value)} className="w-20" /> de {bank.length} questões</div>}
            {f.selection === "manual" && (
              <div className="rounded-lg border border-slate-200 max-h-64 overflow-y-auto iep-scroll divide-y divide-slate-100">
                {bank.length === 0 && <p className="p-3 text-sm text-slate-400">Banco vazio.</p>}
                {bank.map((q, i) => (
                  <label key={q.id} className="flex items-start gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                    <Checkbox checked={f.question_ids.includes(q.id)} onCheckedChange={() => set("question_ids", f.question_ids.includes(q.id) ? f.question_ids.filter((x) => x !== q.id) : [...f.question_ids, q.id])} className="mt-0.5" />
                    <span className="text-slate-700"><span className="text-slate-400 mr-1">{i + 1}.</span>{q.statement}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </Row>
        <Row label="Embaralhar">
          <div className="flex flex-wrap gap-5 text-sm text-slate-700 h-10 items-center">
            <label className="flex items-center gap-2"><Checkbox checked={f.shuffle_questions} onCheckedChange={(v) => set("shuffle_questions", !!v)} /> Ordem das questões</label>
            <label className="flex items-center gap-2"><Checkbox checked={f.shuffle_options} onCheckedChange={(v) => set("shuffle_options", !!v)} /> Ordem das alternativas</label>
          </div>
        </Row>
        <Row label="Tempo limite">
          <Sel value={String(f.time_limit)} onChange={(v) => set("time_limit", parseInt(v, 10))}>
            {[[0, "Sem limite"], [15, "15 minutos"], [30, "30 minutos"], [45, "45 minutos"], [60, "1 hora"], [90, "1h30"], [120, "2 horas"]].map(([v, l]) => <option key={v} value={String(v)}>{l}</option>)}
          </Sel>
        </Row>
        <Row label="Tentativas">
          <div className="flex flex-wrap gap-3 items-center">
            <Sel value={String(f.max_attempts)} onChange={(v) => set("max_attempts", parseInt(v, 10))} testid="exam-attempts">
              {[[1, "1 tentativa"], [2, "2 tentativas"], [3, "3 tentativas"], [5, "5 tentativas"], [0, "Ilimitadas"]].map(([v, l]) => <option key={v} value={String(v)}>{l}</option>)}
            </Sel>
            <Sel value={String(f.wait_hours)} onChange={(v) => set("wait_hours", parseInt(v, 10))}>
              {[[0, "Sem intervalo"], [1, "Intervalo de 1 hora"], [24, "Intervalo de 24 horas"], [72, "Intervalo de 3 dias"]].map(([v, l]) => <option key={v} value={String(v)}>{l}</option>)}
            </Sel>
          </div>
        </Row>
        <Row label="Liberar só após concluir as aulas">
          <div className="flex items-center gap-2 h-10"><Switch checked={f.require_lessons} onCheckedChange={(v) => set("require_lessons", v)} /><span className="text-sm text-slate-600">{f.require_lessons ? "Sim" : "Não"}</span></div>
        </Row>
        <Row label="Exigir aprovação para o certificado">
          <div className="flex items-center gap-2 h-10"><Switch checked={f.required_for_certificate} onCheckedChange={(v) => set("required_for_certificate", v)} /><span className="text-sm text-slate-600">{f.required_for_certificate ? "Sim" : "Não"}</span></div>
        </Row>
        <Row label="Mostrar gabarito ao aluno">
          <Sel value={f.show_answers} onChange={(v) => set("show_answers", v)}>
            <option value="after_pass">Só depois de aprovado(a)</option>
            <option value="after_attempt">Depois de cada tentativa</option>
            <option value="never">Nunca</option>
          </Sel>
        </Row>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="text-white px-8" style={{ background: "#1E9E2A" }} data-testid="exam-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Salvar prova final</>}
        </Button>
      </div>

      {results && (
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-800 mb-3">Resultados</h3>
          <div className="grid grid-cols-3 gap-3 mb-3">
            {[[Users, "Fizeram a prova", results.total], [Trophy, "Aprovados", results.approved], [Percent, "Média (melhor nota)", `${results.average}%`]].map(([I, l, v]) => (
              <div key={l} className="rounded-xl border border-slate-200 p-4"><I className="h-4 w-4 text-[#1E9E2A]" /><div className="font-display text-2xl font-bold text-slate-900 mt-1">{v}</div><div className="text-xs text-slate-500">{l}</div></div>
            ))}
          </div>
          {results.students.length > 0 && (
            <div className="overflow-x-auto iep-scroll rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-slate-50/60">
                  <th className="px-4 py-3">Aluno</th><th className="px-3 py-3">Tentativas</th><th className="px-3 py-3">Melhor nota</th><th className="px-3 py-3">Situação</th><th className="px-4 py-3 text-right">Ações</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {results.students.map((r) => (
                    <tr key={r.user_id}>
                      <td className="px-4 py-2.5"><div className="font-medium text-slate-800">{r.name}</div><div className="text-xs text-slate-400">{r.email}</div></td>
                      <td className="px-3 py-2.5 text-slate-600">{r.attempts}</td>
                      <td className="px-3 py-2.5 font-semibold text-slate-800">{r.best}%</td>
                      <td className="px-3 py-2.5"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${r.passed ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>{r.passed ? "Aprovado" : "Reprovado"}</span></td>
                      <td className="px-4 py-2.5 text-right"><Button size="sm" variant="ghost" onClick={() => reset(r)} title="Liberar novas tentativas"><RotateCcw className="h-4 w-4 mr-1" /> Liberar tentativas</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
